import { translateLabel } from "@/i18n/messages";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HeartIcon, MountainsIcon } from "@phosphor-icons/react";
import { api } from "@/lib/apiClient";
import { AppShell, PageIntro, Surface } from "@/components/layout/AppShell";
import { ParkCard } from "@/components/ParkCard";
import { TrailRow } from "@/components/TrailRow";
import { useAuth } from "@/context/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";
import { parkPath, trailPath } from "@/lib/routes";

export default function Favorites() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (user === false) { navigate("/login"); return; }
    if (user) api.get("/favorites").then(({ data }) => setData(data)).catch(() => setData({ parks: [], trails: [] }));
  }, [user, navigate]);

  const load = () => api.get("/favorites").then(({ data }) => setData(data));

  return (
    <AppShell size="content">
        <PageIntro icon={HeartIcon} title={translateLabel(translateLabel("Want to Ride"))} description={translateLabel("Your saved bike parks and trails.")} />

        {!data ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-64 rounded-2xl bg-white/5" />)}
          </div>
        ) : data.parks.length === 0 && data.trails.length === 0 ? (
          <Surface className="flex flex-col items-center justify-center py-24 text-center">
            <MountainsIcon size={48} className="mb-4 text-zinc-600" />
            <p className="font-display text-2xl font-bold text-white">{translateLabel(translateLabel("No saved rides yet"))}</p>
            <p className="mt-2 max-w-sm text-sm text-zinc-400">{translateLabel(translateLabel("Tap the heart on any bike park or trail to build your weekend ride list."))}</p>
            <button onClick={() => navigate("/")} className="mt-6 rounded-full bg-[#CCFF00] px-6 py-3 text-sm font-bold text-black active:scale-95" data-testid="discover-cta">
              {translateLabel(translateLabel("Discover rides"))}
            </button>
          </Surface>
        ) : (
          <div className="space-y-12">
            {data.parks.length > 0 && (
              <section>
                <h2 className="mb-4 font-display text-2xl font-bold uppercase text-white">{translateLabel(translateLabel("Bike Parks & Centers"))}</h2>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="fav-parks">
                  {data.parks.map((p, i) => (
                    <ParkCard key={p.id} park={p} index={i} onSelect={() => navigate(parkPath(p))} />
                  ))}
                </div>
              </section>
            )}
            {data.trails.length > 0 && (
              <section>
                <h2 className="mb-4 font-display text-2xl font-bold uppercase text-white">{translateLabel("Trails")}</h2>
                <div className="space-y-2.5" data-testid="fav-trails">
                  {data.trails.map((t) => (
                    <TrailRow key={t.id} trail={t} onClick={() => navigate(trailPath(t))} />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
    </AppShell>
  );
}
